import type { ExtraBundle } from "../types";

const en: ExtraBundle = {
  nav: { videos: "Videos", support: "Support" },
  appBadges: {
    downloadOn: "Download on the",
    appStore: "App Store",
    getItOn: "Get it on",
    googlePlay: "Google Play",
    iosApp: "iOS App",
    androidApp: "Android App",
    appStoreAria: "Download FreeCRM on the App Store",
    googlePlayAria: "Get FreeCRM on Google Play",
    takeWithYou: "Take FreeCRM with you",
  },
  footer: {
    tagline: "The free CRM that grows with your business.",
    copyright: "All rights reserved.",
    tagBuiltFor: "Built for teams who deserve better than HubSpot.",
    cols: { product: "Product", company: "Company", resources: "Resources", legal: "Legal" },
    links: {
      features: "Features", pricing: "Pricing", integrations: "Integrations", industries: "Industries",
      about: "About", customers: "Customers", contact: "Contact", support: "Support",
      helpCenter: "Help center", apiDocs: "Docs", privacy: "Privacy", terms: "Terms", media: "Media",
    },
  },
  pricing: {
    eyebrow: "Pricing",
    title: "CRM software pricing",
    subtitle: "US phone numbers only $1.15/month · $0.01 per minute · $0.01 per SMS",
    mostPopular: "Most popular",
    perUserMo: "/user/mo",
    tiers: [
      { name: "Free", price: "$0", blurb: "Free forever — the essentials to manage contacts, deals, and tasks.", cta: "Sign up free" },
      { name: "Pro", price: "$10", blurb: "Full CRM power for growing sales teams.", cta: "Sign up" },
      { name: "Enterprise", price: "$25", blurb: "Advanced features for high-volume teams.", cta: "Sign up" },
    ],
    f: {
      storage: "Unlimited storage", contacts: "Contacts & companies", deals: "Deals & tasks",
      cases: "Cases & calls", calendar: "Calendar & docs", reports: "Reports",
      apps: "iOS & Android apps", campaigns: "Campaigns & forms", email: "Email",
      googleSync: "Google Sync", quickbooks: "QuickBooks", sms: "SMS", voice: "Voice calling",
      creditCard: "Credit card", support: "Customer Support", noSupport: "No Support", api: "API access",
    },
  },
  trust: {
    eyebrow: "Verified claims",
    title: "Security you can verify, not just adjectives",
    subtitle: "Hover any badge to see exactly what it means. No “military-grade” marketing — just the specific standards we’re held to.",
    badges: [
      { label: "TLS 1.3 in transit", short: "Encrypted in transit", tooltip: "Every connection to FreeCRM is encrypted with TLS 1.3 — the latest transport layer security standard — so data exchanged between your browser and our servers cannot be read or tampered with in flight." },
      { label: "AES-256 at rest", short: "Encrypted at rest", tooltip: "All customer data stored in our databases and backups is encrypted at rest using AES-256, the same symmetric cipher used to protect classified information in regulated industries." },
      { label: "SOC 2 Type II", short: "Independently audited", tooltip: "Our infrastructure is hosted in SOC 2 Type II audited data centers. An independent auditor verifies, on an ongoing basis, that controls for security, availability, and confidentiality operate effectively over time." },
      { label: "GDPR & CCPA ready", short: "Privacy-first by default", tooltip: "FreeCRM supports GDPR and CCPA requirements with a signed DPA, data export and deletion tools, regional data residency on Enterprise, and granular role-based access controls." },
    ],
  },
  videos: {
    metaTitle: "FreeCRM video tutorials",
    metaDescription: "Short FreeCRM video tutorials covering setup, custom fields, calendar integration, and the basics of using FreeCRM.",
    pageTitle: "Video tutorials",
    eyebrow: "Video library",
    heading: "FreeCRM video tutorials",
    subtitle: "Short, focused walkthroughs to help you set up FreeCRM, customize forms, sync your calendar, and onboard your team.",
    allVideos: "All videos",
    about: "About this video",
    more: "More tutorials",
    unsupported: "Your browser does not support the video tag.",
    thumbnailAlt: "video thumbnail",
    secUnit: "sec",
    items: {
      "free-crm-user-guide": { name: "FreeCRM user guide", description: "A walkthrough of the FreeCRM interface: navigating contacts, deals, tasks, and the activity stream so new users can get productive in minutes." },
      "company-profile-and-user-setup-guide": { name: "Company profile and user setup guide", description: "How to configure your FreeCRM company profile, invite teammates, and assign roles so your sales team is ready to start working leads." },
      "adding-custom-fields-to-forms": { name: "Adding custom fields to forms", description: "Step-by-step guide to adding custom fields to FreeCRM web forms so you can capture exactly the lead data your sales process needs." },
      "adding-and-customizing-crm-form-fields": { name: "Adding and customizing CRM form fields", description: "Learn how to add, reorder, and customize CRM form fields in FreeCRM to match your team's qualification criteria and pipeline stages." },
      "android-crm-app-user-guide": { name: "Android CRM app user guide", description: "A walkthrough of the FreeCRM Android app: navigating contacts, deals, and tasks on mobile so reps can stay productive on the go." },
      "managing-and-editing-contacts": { name: "Managing and editing contacts", description: "How to create, update, and organize contacts in FreeCRM so your customer records stay clean, current, and easy for the team to action." },
      "crm-calendar-integration-guide": { name: "CRM calendar integration guide", description: "How to connect Google Calendar to FreeCRM so meetings, follow-ups, and scheduled calls stay in sync between your CRM and your calendar." },
    },
  },
  notFound: {
    metaTitle: "Page Not Found — FreeCRM",
    metaDescription: "The page you're looking for doesn't exist. Return to FreeCRM to find what you need.",
    heading: "Oops! Page not found",
    home: "Return to Home",
  },
};

export default en;
